import { useState } from "react";
import { FiCheck, FiX, FiLink, FiRotateCcw, FiCopy } from "react-icons/fi";
import { cn } from "utils/helpers";
import type { ReviewClarification, TriageStatus, DeclineReason } from "../../../../types/clarification-review.type";
import { DeclineReasonModal } from "./DeclineReasonModal";

interface TriageActionsProps {
	clarification: ReviewClarification;
	isTriaging: boolean;
	onMarkDuplicate: (clarificationId: number) => void;
	onTriage: (clarificationId: number, status: TriageStatus, declineReason?: string, declineCategory?: DeclineReason) => void;
	onApplyToAll?: (clarificationId: number) => void;
}

export const TriageActions: React.FC<TriageActionsProps> = ({
	clarification,
	onTriage,
	onMarkDuplicate,
	onApplyToAll,
	isTriaging,
}) => {
	const [showDeclineModal, setShowDeclineModal] = useState(false);

	const isTriaged = clarification.triage_status !== null;

	const handleAccept = () => {
		onTriage(clarification.id, "accepted");
	};

	const handleDeclineConfirm = (reason: string, category: DeclineReason) => {
		onTriage(clarification.id, "declined", reason, category);
		setShowDeclineModal(false);
	};

	const btnBase = "inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors disabled:opacity-50";

	// If already triaged, show the current state with an undo button
	if (isTriaged) {
		return (
			<div className="flex items-center gap-1" data-testid={`triage-actions-${clarification.id}`}>
				{clarification.triage_status === "accepted" && (
					<>
						<button
							type="button"
							onClick={() => setShowDeclineModal(true)}
							disabled={isTriaging}
							className={cn(btnBase, "text-red-600 hover:bg-red-50 border border-red-200")}
							title="Change to declined"
							data-testid={`change-decline-btn-${clarification.id}`}
						>
							<FiX size={12} />
							Decline
						</button>
						{onApplyToAll && !clarification.source_clarification_id && (
							<button
								type="button"
								onClick={() => onApplyToAll(clarification.id)}
								disabled={isTriaging}
								className={cn(btnBase, "text-blue-600 hover:bg-blue-50 border border-blue-200")}
								title="Apply to all suppliers"
								data-testid={`apply-to-all-btn-${clarification.id}`}
							>
								<FiCopy size={12} />
								All
							</button>
						)}
					</>
				)}
				{clarification.triage_status === "declined" && (
					<button
						type="button"
						onClick={handleAccept}
						disabled={isTriaging}
						className={cn(btnBase, "text-green-600 hover:bg-green-50 border border-green-200")}
						title="Change to accepted"
						data-testid={`change-accept-btn-${clarification.id}`}
					>
						<FiCheck size={12} />
						Accept
					</button>
				)}
				{clarification.triage_status === "duplicate" && (
					<button
						type="button"
						onClick={handleAccept}
						disabled={isTriaging}
						className={cn(btnBase, "text-green-600 hover:bg-green-50 border border-green-200")}
						title="Undo duplicate — accept instead"
						data-testid={`undo-duplicate-btn-${clarification.id}`}
					>
						<FiRotateCcw size={12} />
						Undo
					</button>
				)}
				{showDeclineModal && (
					<DeclineReasonModal
						isOpen={showDeclineModal}
						count={1}
						onClose={() => setShowDeclineModal(false)}
						onConfirm={handleDeclineConfirm}
						isLoading={isTriaging}
					/>
				)}
			</div>
		);
	}

	// Pending — show full action buttons
	return (
		<div className="flex items-center gap-1" data-testid={`triage-actions-${clarification.id}`}>
			<button
				type="button"
				onClick={handleAccept}
				disabled={isTriaging}
				className={cn(btnBase, "text-green-700 hover:bg-green-50 border border-green-200")}
				title="Accept"
				data-testid={`accept-btn-${clarification.id}`}
			>
				<FiCheck size={12} />
				Accept
			</button>
			<button
				type="button"
				onClick={() => setShowDeclineModal(true)}
				disabled={isTriaging}
				className={cn(btnBase, "text-red-700 hover:bg-red-50 border border-red-200")}
				title="Decline"
				data-testid={`decline-btn-${clarification.id}`}
			>
				<FiX size={12} />
				Decline
			</button>
			<button
				type="button"
				onClick={() => onMarkDuplicate(clarification.id)}
				disabled={isTriaging}
				className={cn(btnBase, "text-gray-600 hover:bg-gray-50 border border-gray-200")}
				title="Mark as duplicate"
				data-testid={`duplicate-btn-${clarification.id}`}
			>
				<FiLink size={12} />
				Duplicate
			</button>

			{showDeclineModal && (
				<DeclineReasonModal
					isOpen={showDeclineModal}
					count={1}
					onClose={() => setShowDeclineModal(false)}
					onConfirm={handleDeclineConfirm}
					isLoading={isTriaging}
				/>
			)}
		</div>
	);
};
